import { type Client, type Sink, createClient, type ClientOptions } from 'graphql-ws';
import { type Operation, type FetchResult, ApolloLink, Observable } from 'apollo-link';
import { type FormattedExecutionResult, print } from 'graphql';
import { ApolloError } from 'apollo-client';

import { fg } from '@confluence/feature-gating';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';

import { getInternetConnectionState } from './getInternetConnectionState';
import { getBackOffDelay, getDelayRangeFromGraphqlWebsocketState } from './getBackOffDelay';
import { calculateBackOffRange } from './calculateBackOffRange';

export const KEEP_ALIVE = 600000;

export class GraphQLWSLink extends ApolloLink {
	public client: Client;
	private internetConnectionState: 'healthy' | 'unhealthy' | null = 'healthy';
	private timedOut: ReturnType<typeof setTimeout> | null = null;

	private closedInfo: CloseEvent | null = null;
	private replayQueries: Record<string, { unsubscribe: () => void; query: string }> = {};

	private currentGetInternetConnectionStatePromise: ReturnType<
		typeof getInternetConnectionState
	> | null = null;

	constructor(options: ClientOptions) {
		super();
		this.client = createClient({
			...options,
			retryWait: this.retryWait,
			on: {
				connected: this.onConnected,
				closed: (info: any) => {
					this.closedInfo = info;
				},
				ping: (received) => {
					if (!received /* sent */) {
						this.timedOut = setTimeout(() => {
							if (fg('confluence_enable_agg_subscription_link_replay')) {
								// unsubscribe so that we can reconnect with new arguments
								for (const key in this.replayQueries) {
									// when this gate is enabled it unsubscribes queries so that we can reconnect
									// with new arguments
									if (this.replayQueries[key]) {
										this.replayQueries[key].unsubscribe();
										delete this.replayQueries[key];
									}
								}
								this.client.terminate();
							}
						}, KEEP_ALIVE / 2);
					}
				},
				pong: (received) => {
					if (received && this.timedOut !== null) {
						clearTimeout(this.timedOut);
					}
				},
			},
		});
	}

	private onConnected = (_socket: any, _payload: any, wasRetry: any) => {
		void getAnalyticsWebClient().then((client) => {
			client.sendOperationalEvent({
				source: 'confluence-AGGWebSocketLink',
				actionSubject: 'webSocket',
				action: wasRetry ? 'reconnected' : 'connected',
			});
		});
	};

	private verifyAndUpdateInternetHealth = async () => {
		this.currentGetInternetConnectionStatePromise =
			this.currentGetInternetConnectionStatePromise || getInternetConnectionState();
		const internetHealthState = await this.currentGetInternetConnectionStatePromise;
		this.internetConnectionState = internetHealthState;
		this.currentGetInternetConnectionStatePromise = null;
	};

	private verifyAndUpdateInternetHealthUntilHealthy = async (): Promise<void> => {
		await this.verifyAndUpdateInternetHealth();

		if (this.internetConnectionState === 'healthy') {
			return;
		} else {
			this.client?.terminate();
		}

		await new Promise((resolve) => {
			setTimeout(resolve, 500);
		});
		return this.verifyAndUpdateInternetHealthUntilHealthy();
	};

	private retryWait = async (retries: number) => {
		const retryDelayRange = await getDelayRangeFromGraphqlWebsocketState(retries);
		// This is the logic reused from legacy WebSocket with a check verifyAndUpdateInternetHealthUntilHealthy
		// If the internet connection is healthy then we can retry, otherwise we do not need to retry
		// We subtract the time spent waiting to reconnect from the exponential backoff
		const startedAt = Date.now();
		await this.verifyAndUpdateInternetHealthUntilHealthy();
		// since we took some time to verify internet health, reduce the exponential backoff
		const msCheckingInternetHealth = Date.now() - startedAt;
		const retryDelay = getBackOffDelay({
			range: retryDelayRange,
			timeSpent: msCheckingInternetHealth,
		});

		// monitor status looks at if a user is idle or hidden to determine the retry delay
		await new Promise((resolve) => {
			let timeoutId = setTimeout(resolve, retryDelay);
			const newRetryDelayRange = calculateBackOffRange({
				status: 'active',
				attemptsCount: retries,
			});

			const msAlreadyWaited = Date.now() - startedAt;
			const newRetryDelay = getBackOffDelay({
				range: newRetryDelayRange,
				timeSpent: msAlreadyWaited,
			});

			const actualReconnectionDelay = Math.max(newRetryDelay - msAlreadyWaited, 0);
			// Clear old timeout and set a new one with the updated delay
			clearTimeout(timeoutId);
			timeoutId = setTimeout(resolve, actualReconnectionDelay);
		});
	};

	public request(operation: Operation): Observable<FetchResult> {
		// Add null check for operation.getContext to prevent "Cannot read properties of undefined" errors
		const context = operation.getContext?.() || {};
		// context is coming from a previous link since we are using apollo v2
		// subscriptions does not have context or extensions in some cases
		return new Observable((observer) => {
			const subscription = this.client.subscribe<FetchResult>(
				{
					...operation,
					query: print(operation.query),
					extensions: context?.extensions,
				},
				{
					next: (result: FetchResult) => {
						// Transform null data to empty object
						const transformedResult: FetchResult = {
							...result,
							data: result.data === null ? {} : result.data,
						};
						observer.next(transformedResult);
					},
					complete: observer.complete.bind(observer),
					error: (err: any) => {
						if (this.closedInfo && !this.closedInfo?.wasClean && !err?.message) {
							void getAnalyticsWebClient().then((client) => {
								client.sendOperationalEvent({
									source: 'confluence-AGGWebSocketLink',
									actionSubject: 'webSocket',
									action: 'closed-socket-error',
									attributes: {
										reason: this.closedInfo?.reason,
										code: this.closedInfo?.code,
										wasClean: this.closedInfo?.wasClean,
									},
								});
							});
						}
						if (err instanceof Error) {
							return observer.error(err);
						}
						return observer.error(
							new ApolloError({
								graphQLErrors: Array.isArray(err) ? err : [err],
							}),
						);
					},
					// casting around a wrong type in graphql-ws, which incorrectly expects `Sink<ExecutionResult>`
				} satisfies Sink<FormattedExecutionResult> as any,
			);
			// keep track of the subscription so we can unsubscribe later
			this.replayQueries[operation.operationName] = {
				unsubscribe: subscription,
				query: print(operation.query),
			};
			return subscription;
		});
	}
}
